/**
 * MocapNode — CMU Motion Capture Playback
 *
 * Loads ASF/AMC files and outputs animated LineSegments geometry via buildMesh().
 * Animation runs via animate() callback (registered by scene descriptor).
 * Forward kinematics computes joint world positions each frame,
 * updating line geometry in-place.
 *
 * Data: CMU Motion Capture ASF/AMC files (http://mocap.cs.cmu.edu/).
 * Served at /mocaps/all_asfamc/subjects/{nn}/{nn}.asf and {nn}_{cc}.amc.
 * In dev, place files in project root `mocaps/` (Vite serves them).
 * In production, host on S3/CDN and configure the base URL accordingly.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';
import { parseASF, type Skeleton } from './asfParser';
import { parseAMC, type MotionClip } from './amcParser';
import { computeFK } from './forwardKinematics';
import { MOCAP_CATALOG, SUBJECTS, SUBJECT_LABELS, SUBJECT_INFO, CLIP_LABELS, CLIP_INFO } from './mocapCatalog';
import { logger } from '@/logging';

/** Build default clip options from first subject */
const DEFAULT_SUBJECT = '01';
const defaultClips = MOCAP_CATALOG[DEFAULT_SUBJECT];
const DEFAULT_CLIP_OPTIONS = defaultClips.map(n => String(n).padStart(2, '0'));

const mocapEndpoints = {
  ...internals({
    meshOut: { dataType: 'three:group' as const, isOutput: true }
  }),
  ...params({
    subject: { order:1,group: 'Animation',dataType: 'string' as const,  options: SUBJECTS, optionLabels: SUBJECT_LABELS, optionInfo: SUBJECT_INFO },
    clip: { order:2,group: 'Animation',dataType: 'string' as const,  options: DEFAULT_CLIP_OPTIONS, optionLabels: CLIP_LABELS[DEFAULT_SUBJECT], optionInfo: CLIP_INFO[DEFAULT_SUBJECT] },
    speed: { value: 1, min: 0, max: 4, step: 0.01 },
    playing: { dataType: 'boolean' as const, value: true },
    scale: { value: 1, min: 0.5, max: 1.5, step: 0.01 },
  })
};

/** Cache parsed skeletons by subject number */
const skeletonCache = new Map<string, Skeleton>();

class MocapNode extends BaseNode<typeof mocapEndpoints> {
  private three: Three;
  private storedMesh: any = null;
  private rebuildCallback: (() => void) | null = null;

  private skeleton: Skeleton | null = null;
  private motionClip: MotionClip | null = null;
  private currentFrame = 0;
  private frameAccumulator = 0;
  private lastTime = 0;
  private loading = false;
  private loadFailed = false;

  private loadedSubject = '';
  private loadedClip = '';

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  initialize(): void {
    super.initialize();
    this.updateClipOptions();
    this.loadData();
    this.subscribeToTransport();
  }

  dispose(): void {
    this.unsubscribeFromTransport();
    super.dispose();
  }

  hasFunctionParams(): boolean {
    return true;
  }

  buildMesh(): any {
    if (!this.skeleton || !this.motionClip || this.motionClip.frameCount === 0) {
      return null;
    }

    const frame = this.motionClip.frames[this.currentFrame % this.motionClip.frameCount];
    const scale = this.params.scale.value;
    const positions = computeFK(this.three, this.skeleton, frame, scale);
    return this.buildLineSegments(positions);
  }

  setThreeObject(mesh: any): void {
    this.storedMesh = mesh;
  }

  setRebuildCallback(callback: () => void): void {
    this.rebuildCallback = callback;
  }

  animate(): void {
    if (!this.skeleton || !this.motionClip || this.motionClip.frameCount === 0) return;

    const subject = this.params.subject.value;
    const clip = this.params.clip.value;
    if (subject !== this.loadedSubject || clip !== this.loadedClip) {
      if (!this.loadFailed) this.loadData();
      return;
    }

    const playing = this.params.playing.value;
    if (!playing) return;

    const now = performance.now();
    if (this.lastTime === 0) {
      this.lastTime = now;
      return;
    }
    const dt = (now - this.lastTime) / 1000;
    this.lastTime = now;

    const speed = this.params.speed.value;
    // Source data is 120fps
    this.frameAccumulator += dt * 120 * speed;
    const framesToAdvance = Math.floor(this.frameAccumulator);
    if (framesToAdvance === 0) return;
    this.frameAccumulator -= framesToAdvance;

    this.currentFrame = (this.currentFrame + framesToAdvance) % this.motionClip.frameCount;

    const scale = this.params.scale.value;
    const frame = this.motionClip.frames[this.currentFrame];
    const jointPositions = computeFK(this.three, this.skeleton, frame, scale);

    if (this.storedMesh) {
      this.updateMeshGeometry(jointPositions);
    }
  }

  onParamChanged(): void {
    const subject = this.params.subject.value;
    const clip = this.params.clip.value;

    // Update clip dropdown when subject changes
    this.updateClipOptions();

    if (subject !== this.loadedSubject || clip !== this.loadedClip) {
      this.loadFailed = false;
      this.loadData();
    }
  }

  private updateClipOptions(): void {
    const subject = this.params.subject.value;
    const clips = MOCAP_CATALOG[subject];
    if (!clips) return;

    const options = clips.map(n => String(n).padStart(2, '0'));
    this.setEndpointMetadata('clip', {
      options,
      optionLabels: CLIP_LABELS[subject] || options,
      optionInfo: CLIP_INFO[subject] || options,
    });
    this.params.clip.set(options[0])
  }

  private async loadData(): Promise<void> {
    if (this.loading) return;
    this.loading = true;

    const subject = this.params.subject.value;
    let clipNum = parseInt(this.params.clip.value, 10);

    // Validate subject exists in catalog
    const availableClips = MOCAP_CATALOG[subject];
    if (!availableClips) {
      logger.error(`[MocapNode] Subject ${subject} not in catalog`);
      this.loadFailed = true;
      this.loading = false;
      return;
    }

    // Auto-correct clip to first available if requested clip doesn't exist
    if (!availableClips.includes(clipNum)) {
      clipNum = availableClips[0];
      logger.trace(`[MocapNode] Clip not available for subject ${subject}, using clip ${clipNum}`);
    }

    const padded = subject.padStart(2, '0');
    const clipPadded = String(clipNum).padStart(2, '0');

    try {
      // Load skeleton (cached per subject)
      let skeleton = skeletonCache.get(subject);
      if (!skeleton) {
        const asfUrl = `/mocaps/all_asfamc/subjects/${padded}/${padded}.asf`;
        const asfResp = await fetch(asfUrl);
        if (!asfResp.ok) {
          throw new Error(`Failed to load ASF: ${asfUrl} (${asfResp.status})`);
        }
        skeleton = parseASF(await asfResp.text());
        skeletonCache.set(subject, skeleton);
      }

      // Load motion clip
      const amcUrl = `/mocaps/all_asfamc/subjects/${padded}/${padded}_${clipPadded}.amc`;
      const amcResp = await fetch(amcUrl);
      if (!amcResp.ok) {
        throw new Error(`Failed to load AMC: ${amcUrl} (${amcResp.status})`);
      }
      const motionClip = parseAMC(await amcResp.text());

      this.skeleton = skeleton;
      this.motionClip = motionClip;
      this.currentFrame = 0;
      this.frameAccumulator = 0;
      this.lastTime = 0;
      this.loadedSubject = subject;
      this.loadedClip = this.params.clip.value;

      logger.trace(`[MocapNode] Loaded subject ${padded} clip ${clipPadded}: ${motionClip.frameCount} frames, ${skeleton.bones.size} bones`);

      this.rebuildCallback?.();
    } catch (err) {
      logger.error(`[MocapNode] ${err}`);
      this.loadFailed = true;
    } finally {
      this.loading = false;
    }
  }

  private buildLineSegments(jointPositions: Map<string, { x: number; y: number; z: number }>): any {
    const vertices: number[] = [];

    // Draw line segments for each parent→child bone
    for (const [parent, children] of this.skeleton!.hierarchy) {
      const parentPos = jointPositions.get(parent);
      if (!parentPos) continue;
      for (const child of children) {
        const childPos = jointPositions.get(child);
        if (!childPos) continue;
        vertices.push(parentPos.x, parentPos.y, parentPos.z);
        vertices.push(childPos.x, childPos.y, childPos.z);
      }
    }

    const geometry = new this.three.BufferGeometry();
    geometry.setAttribute('position', new this.three.Float32BufferAttribute(vertices, 3));
    const material = new this.three.LineBasicMaterial({ color: 0x00ff88 });
    return new this.three.LineSegments(geometry, material);
  }

  private updateMeshGeometry(jointPositions: Map<string, { x: number; y: number; z: number }>): void {
    const vertices: number[] = [];

    for (const [parent, children] of this.skeleton!.hierarchy) {
      const parentPos = jointPositions.get(parent);
      if (!parentPos) continue;
      for (const child of children) {
        const childPos = jointPositions.get(child);
        if (!childPos) continue;
        vertices.push(parentPos.x, parentPos.y, parentPos.z);
        vertices.push(childPos.x, childPos.y, childPos.z);
      }
    }

    const mesh = this.storedMesh;
    const posAttr = mesh.geometry?.attributes?.position;

    if (posAttr && posAttr.count === vertices.length / 3) {
      // Update in-place
      const arr = posAttr.array as Float32Array;
      for (let i = 0; i < vertices.length; i++) {
        arr[i] = vertices[i];
      }
      posAttr.needsUpdate = true;
    } else {
      // Vertex count changed — swap geometry
      const oldGeo = mesh.geometry;
      const newGeo = new this.three.BufferGeometry();
      newGeo.setAttribute('position', new this.three.Float32BufferAttribute(vertices, 3));
      mesh.geometry = newGeo;
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
  }

  private subscribeToTransport(): void {
     this.params.playing.set(this.transport.state==='started');
    this.transport.on('start', this.boundStart);
    this.transport.on('stop', this.boundStop);
    this.transport.on('pause', this.boundStop);
  }

  private unsubscribeFromTransport(): void {
    this.transport.off('start', this.boundStart);
    this.transport.off('stop', this.boundStop);
    this.transport.off('pause', this.boundStop);
  }

  private boundStart = () => { this.params.playing.set(true); };
  private boundStop = () => { this.params.playing.set(false); };
}

export const mocapNodeDef: NodeDef<MocapNode> = {
  type: 'Mocap',
  shortName: 'mocap',
  category: 'geometry',
  info: 'Plays back CMU Motion Capture skeleton animations as 3D line geometry.',
  endpoints: mocapEndpoints,
  ctor: MocapNode
};
